import { Injectable } from '@angular/core';
import { ValidationErrors } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { combineAllWith } from '@shared/combineAllWith';
import { readFiles } from '@shared/fileReaderUtils';
import { IBase } from '@shared/models/api/base';
import { filterMap } from '@shared/utils/rxjs';
import { catchError, forkJoin, from, map, mergeMap, Observable, of, toArray } from 'rxjs';
import { ExcelFileTransfer, xlsxImportError } from '../file-transfer-services/excelFileTransfer.service';
import { IFileDataSchema } from '../file-transfer-services/interfaces';
import { IPallet, ListResponse, PalletisingService } from '../palletising.service';
import { PalletizingPalletValidationService } from '../Validation/palletizingPalletValidation.service';
import { FailedParse, FailedValidationImport, ImportErrorTypes, ImportResponse, ImportStatus, ITransferService, SuccessFullImport } from './interfaces';

const sheetName = 'Pallets';
const importConcurrency = 5;

const PalletSchema: IFileDataSchema<IPalletExportData> = {
	name: sheetName,
	properties: {
		name: { type: 'string', required: true, fileKey: 'Name' },
		description: { type: 'string', required: false, fileKey: 'Description' },
		length: { type: 'number', required: true, fileKey: 'Length' },
		width: { type: 'number', required: true, fileKey: 'Width' },
		height: { type: 'number', required: true, fileKey: 'Height' },
		dimension: { type: 'string', required: false, fileKey: 'Dimension' },
		type: { type: 'string', required: false, fileKey: 'Type' },
		zipCodeRule: { type: 'number', required: false, fileKey: 'ZipCodeRule' },
		weight: { type: 'number', required: false, fileKey: 'Weight' },
		maxHeight: { type: 'number', required: false, fileKey: 'MaxHeight' },
		maxWeight: { type: 'number', required: false, fileKey: 'MaxWeight' },
		oversize: { type: 'number', required: false, fileKey: 'Oversize' },
		spaceBetween: { type: 'number', required: false, fileKey: 'SpaceBetween' },
		maxLayers: { type: 'number', required: false, fileKey: 'MaxLayers' },
		active: { type: 'boolean', required: false, fileKey: 'Active' },
	},
};

export interface PalletisingImportContext {
	pallets: IPallet[];
	palletTypes: IBase[];
	palletDimensions: IBase[];
	zipCodeRules: number[];
}

@Injectable({
	providedIn: 'root',
})
export class PalletisingPalletTransferService implements ITransferService<IPallet> {
	constructor(
		private palletisingService: PalletisingService,
		private palletValidationService: PalletizingPalletValidationService,
		private excelFileTransfer: ExcelFileTransfer<IPalletExportData>,
		private translator: TranslateService,
	) {}

	// PalletSchema carries no comment/example/width metadata, so it has no template to offer yet
	public hasTemplate = () => false;
	public getTemplate = (): void => {
		throw new Error('Pallets offer no import template; check hasTemplate() before calling getTemplate().');
	};

	public exportData(pallets: IPallet[]) {
		return from(pallets).pipe(
			combineAllWith(forkJoin([this.palletisingService.fetchPalletTypes(), this.palletisingService.fetchPalletDimensions()])),
			map((data) => this.mapPalletToExportData(data)),
			toArray(),
			mergeMap((data) => {
				return this.excelFileTransfer.exportData(sheetName, data, PalletSchema);
			}),
			map(() => pallets), // Return the original pallet array after export
			catchError((error) => {
				throw new Error(`Failed to export data: ${error.message}`);
			}),
		);
	}

	mapPalletToExportData([pallet, [palletTypes, palletDimensions]]: [pallet: IPallet, [palletTypes: ListResponse<IBase>, palletDimensions: ListResponse<IBase>]]): IPalletExportData {
		const dimension = palletDimensions.items.find((d) => d.self === pallet.dimension?.self);
		const type = palletTypes.items.find((t) => t.self === pallet.type?.self);

		// The grouping was deleted after the pallet was loaded - fail the export instead of writing a file the pallet cannot be imported from
		if (pallet.dimension && !dimension) {
			throw new Error(this.translator.instant('masterdata.palletising.pallets.export.errors.missing-dimension', { name: pallet.name }));
		}

		if (pallet.type && !type) {
			throw new Error(this.translator.instant('masterdata.palletising.pallets.export.errors.missing-type', { name: pallet.name }));
		}

		return {
			name: pallet.name,
			description: pallet.description,
			length: pallet.configuration.length,
			width: pallet.configuration.width,
			height: pallet.configuration.height,
			dimension: dimension?.name ?? null,
			type: type?.name ?? null,
			zipCodeRule: pallet.zipCodeRule,
			weight: pallet.configuration.weight,
			maxHeight: pallet.configuration.maxHeight,
			maxWeight: pallet.configuration.maxWeight,
			oversize: pallet.configuration.oversize,
			spaceBetween: pallet.configuration.spaceBetween,
			maxLayers: pallet.configuration.maxLayers,
			active: pallet.configuration.active,
		} as IPalletExportData;
	}

	importData(file: File | File[], overwrite: boolean = false) {
		const ctx: PalletisingImportContext = {
			pallets: [],
			palletTypes: [],
			palletDimensions: [],
			zipCodeRules: [],
		};

		const ImportErrors: ImportErrorTypes[] = [];
		if (!Array.isArray(file)) {
			// If a single file is provided, we convert it to an array
			file = [file];
		}

		const ob = readFiles(file, 'binary').pipe(
			mergeMap(this.importFileData(ImportErrors)),
			combineAllWith(
				forkJoin([this.palletisingService.fetchPallets(), this.palletisingService.fetchPalletTypes(), this.palletisingService.fetchPalletDimensions(), this.palletisingService.fetchZipCodes()]).pipe(
					map(([pallets, palletTypes, palletDimensions, zipCodes]) => {
						ctx.pallets = pallets.items;
						ctx.palletTypes = palletTypes.items;
						ctx.palletDimensions = palletDimensions.items;
						// The dialog only offers the rule numbers that exist in the zip code register
						ctx.zipCodeRules = [...new Set(zipCodes.items.map((zipCode) => zipCode.ruleNumber))];
						return {}; // We don't need to emit anything from this observable
					}),
				),
			),
			map(([pallet, _]) => pallet),
			// Resolve the dimension and type names to their groupings and check the zip code rule exists
			filterMap(this.validateAndMapReferences(ctx, ImportErrors)),
			filterMap(this.validatePallet(ImportErrors)),
			// A pallet register holds hundreds of pallets, so the requests are limited instead of being sent all at once
			mergeMap(this.createOrUpdatePallet(ctx, overwrite, ImportErrors), importConcurrency),
			toArray(),
			map((pallets) => {
				const successfulImports = pallets.map((p) => ({ Id: p.entity.name, status: p.status }) as SuccessFullImport);

				return {
					successfulImports,
					errors: ImportErrors,
				} as ImportResponse;
			}),
		);

		return ob;
	}

	importFileData(ImportErrors: ImportErrorTypes[]) {
		return (buffer: ArrayBuffer): Observable<IPalletExportData> => {
			const errorList: xlsxImportError[] = [];

			try {
				const rows = this.excelFileTransfer.importData(buffer, PalletSchema, errorList).map((imported) => imported.data);

				if (errorList.length > 0) {
					// If there are errors, we throw an error with the list of errors
					errorList.forEach((e) => {
						ImportErrors.push({
							message: e.error,
							status: ImportStatus.ParsingError,
						} as FailedParse);
					});
				}

				return from(rows);
			} catch (error) {
				ImportErrors.push({
					status: ImportStatus.ParsingError,
					message: error.message,
				} as FailedParse);
				return of();
			}
		};
	}

	validateAndMapReferences(ctx: PalletisingImportContext, ImportErrors: ImportErrorTypes[]) {
		return (pallet: IPalletExportData): IPallet | undefined => {
			const dimension = ctx.palletDimensions.find((d) => d.name.toLowerCase() === pallet.dimension?.toLowerCase());
			const type = ctx.palletTypes.find((t) => t.name.toLowerCase() === pallet.type?.toLowerCase());
			const zipCodeRule = ctx.zipCodeRules.find((rule) => rule === pallet.zipCodeRule) ?? null;

			const errors: Record<string, ValidationErrors> = {};

			if (pallet.dimension && !dimension) {
				errors['dimension'] = {
					required: this.translator.instant('masterdata.palletising.pallets.import.errors.missing-dimension', { dimension: pallet.dimension }),
				};
			}

			if (pallet.type && !type) {
				errors['type'] = {
					required: this.translator.instant('masterdata.palletising.pallets.import.errors.missing-type', { type: pallet.type }),
				};
			}

			// A pallet must not end up pointing at a zip code rule that does not exist.
			// Rule 0 is falsy but valid, so the lookup is checked against null rather than by truthiness
			if (pallet.zipCodeRule !== undefined && pallet.zipCodeRule !== null && zipCodeRule === null) {
				errors['zipCodeRule'] = {
					required: this.translator.instant('masterdata.palletising.pallets.import.errors.missing-zipcode-rule', { zipCodeRule: pallet.zipCodeRule }),
				};
			}

			if (Object.keys(errors).length > 0) {
				const error: FailedValidationImport = {
					Id: `${pallet.name}`,
					status: ImportStatus.ValidationError,
					errors,
				};

				ImportErrors.push(error);
				return undefined;
			}

			return {
				name: pallet.name,
				description: pallet.description ?? null,
				configuration: {
					length: pallet.length,
					width: pallet.width,
					height: pallet.height,
					weight: pallet.weight ?? null,
					maxHeight: pallet.maxHeight ?? null,
					maxWeight: pallet.maxWeight ?? null,
					oversize: pallet.oversize ?? null,
					spaceBetween: pallet.spaceBetween ?? null,
					maxLayers: pallet.maxLayers ?? null,
					// An empty Active column means the same as creating a pallet in the dialog, which defaults to active
					active: pallet.active ?? true,
				},
				dimension: dimension ? { self: dimension.self } : null,
				type: type ? { self: type.self } : null,
				zipCodeRule,
				self: null,
				meta: null,
			};
		};
	}

	validatePallet(ImportErrors: ImportErrorTypes[]) {
		return (pallet: IPallet): IPallet | undefined => {
			// Validate the pallet, the self is assigned later so it is not validated here
			const validationErrors = this.palletValidationService.validatePallet(pallet, false);
			if (Object.keys(validationErrors).length > 0) {
				ImportErrors.push({
					Id: `${pallet.name}`,
					status: ImportStatus.ValidationError,
					errors: validationErrors,
				});
				return undefined;
			}

			return pallet;
		};
	}

	createOrUpdatePallet(ctx: PalletisingImportContext, overwrite: boolean, ImportErrors: ImportErrorTypes[]) {
		return (pallet: IPallet) => {
			const existingPallet = ctx.pallets.find((p) => p.name.toLowerCase() === pallet.name.toLowerCase());
			if (existingPallet) {
				if (!overwrite) {
					const message = this.translator.instant('masterdata.palletising.pallets.import.errors.exists', { name: pallet.name });
					// Do not overwrite existing pallet
					ImportErrors.push({
						Id: pallet.name,
						status: ImportStatus.Skipped,
						message,
					});
					return of();
				}
				// preserve the self of the existing pallet so it is updated instead of created
				pallet.self = existingPallet.self;
				// Update existing pallet
				return this.palletisingService.updatePallet(pallet).pipe(
					map(() => ({
						entity: pallet,
						status: ImportStatus.SuccessUpdate,
					})),

					catchError((error) => {
						const message = this.translator.instant('masterdata.palletising.pallets.import.errors.update-failed', { name: pallet.name, msg: error.message });
						ImportErrors.push({
							Id: pallet.name,
							status: ImportStatus.Failed,
							message,
						});
						return of();
					}),
				);
			} else {
				// Create new pallet
				return this.palletisingService.createPallet(pallet).pipe(
					map(() => ({
						entity: pallet,
						status: ImportStatus.SuccessCreate,
					})),

					catchError((error) => {
						const message = this.translator.instant('masterdata.palletising.pallets.import.errors.create-failed', { name: pallet.name, msg: error.message });
						ImportErrors.push({
							Id: pallet.name,
							status: ImportStatus.Failed,
							message,
						});
						return of();
					}),
				);
			}
		};
	}
}

export interface IPalletExportData {
	name: string;
	description?: string;
	length: number;
	width: number;
	height: number;
	dimension?: string;
	type?: string;
	zipCodeRule?: number;
	weight?: number;
	maxHeight?: number;
	maxWeight?: number;
	oversize?: number;
	spaceBetween?: number;
	maxLayers?: number;
	active?: boolean;
}
